import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Nav,
  NavItem,
  NavLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
} from "reactstrap";

// ====== Your pages (as requested) ======
import LowStockAlert from "./LowStockAlert.js";
import PurchaseOrderViewList from "./PurchaseOrderList";
import PendingPurchase from "./PendingPurchase.js";
import PurchaseInvoice from "./PurchaseInvoice.js";
import PurchaseReturn from "./PurchaseReturn.js";
import DebitNoteList from "./DebitNote.js";

/* ---------------- Inline CSS (inject once) ---------------- */
const injectOnce = () => {
  if (document.getElementById("purchase-tabs-inline-css")) return;
  const s = document.createElement("style");
  s.id = "purchase-tabs-inline-css";
  s.textContent = `
    .pt-wrap{ }
    .pt-card{ border:1px solid #e5e7eb; border-radius:14px; }
    .pt-nav{
      display:flex; gap:8px; flex-wrap:wrap; align-items:center;
      border:1px solid #e5e7eb; border-radius:14px; background:#f4f7f6;
      padding:6px 8px;
    }
    .pt-pill{
      display:inline-flex; align-items:center; justify-content:center;
      padding:8px 14px; border-radius:999px; cursor:pointer; user-select:none;
      font-weight:700; letter-spacing:.02em; border:1px solid #e5e7eb; background:#fff;
    }
    .pt-pill.active{
      color:#fff; background:#0b5cf6; border-color:#0b5cf6;
      box-shadow: 0 8px 18px rgba(11,92,246,.2);
    }
    .pt-actions{ display:flex; gap:8px; margin-left:auto; }
    .pt-rename-body .rowline{ display:flex; gap:10px; align-items:center; margin-bottom:8px; }
    .pt-rename-body .rowline label{ width:160px; margin:0; font-weight:600; color:#0f172a; }
    .pt-content{ padding:8px; }
  `;
  document.head.appendChild(s);
};

/* ---------------- Constants ---------------- */
const LS_LABELS_KEY = "PurchaseTabsLabels";
const LS_ACTIVE_KEY = "PurchaseTabsActive";

const DEFAULT_TABS = [
  { key: "lowStock", defaultLabel: "Low Stock", Component: LowStockAlert },
  {
    key: "placed",
    defaultLabel: "Placed Orders",
    Component: PurchaseOrderViewList,
  },
  // {
  //   key: "confirmed",
  //   defaultLabel: "Confirmed Order",
  //   Component: PendingPurchase,
  // },
  { key: "invoiced", defaultLabel: "Invoiced", Component: PurchaseInvoice },
  // { key: "returned", defaultLabel: "Returned", Component: PurchaseReturn },
  { key: "debitnote", defaultLabel: "Debit Note", Component: DebitNoteList },
];

/* ---------------- Helper: load/save labels ---------------- */
const loadLabels = () => {
  try {
    const raw = localStorage.getItem(LS_LABELS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object") return parsed;
  } catch {}
  // default mapping {key: label}
  const m = {};
  DEFAULT_TABS.forEach((t) => (m[t.key] = t.defaultLabel));
  return m;
};

const saveLabels = (labels) => {
  localStorage.setItem(LS_LABELS_KEY, JSON.stringify(labels || {}));
};

const loadActive = () =>
  localStorage.getItem(LS_ACTIVE_KEY) || DEFAULT_TABS[0].key;
const saveActive = (key) => localStorage.setItem(LS_ACTIVE_KEY, key);

/* ======================= Component ======================= */
export default function PurchaseTabs() {
  useEffect(() => injectOnce(), []);

  // labels state
  const [labels, setLabels] = useState(loadLabels());
  // active tab
  const [active, setActive] = useState(loadActive());
  // rename modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editLabels, setEditLabels] = useState(labels);

  // derive final tab list with labels applied
  const tabs = useMemo(() => {
    return DEFAULT_TABS.map((t) => ({
      ...t,
      label: labels?.[t.key] || t.defaultLabel,
    }));
  }, [labels]);

  const activeTab = useMemo(
    () => tabs.find((t) => t.key === active) || tabs[0],
    [tabs, active],
  );

  const toggleTab = (key) => {
    setActive(key);
    saveActive(key);
  };

  /* -------- Rename Modal handlers -------- */
  const openRename = () => {
    setEditLabels({ ...labels });
    setModalOpen(true);
  };
  const closeRename = () => setModalOpen(false);

  const handleLabelChange = (key, value) => {
    setEditLabels((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveLabels = () => {
    // ensure empty labels fallback to defaults
    const next = { ...editLabels };
    DEFAULT_TABS.forEach((t) => {
      if (!next[t.key] || !next[t.key].trim()) next[t.key] = t.defaultLabel;
    });
    setLabels(next);
    saveLabels(next);
    setModalOpen(false);
  };

  const handleResetLabels = () => {
    const m = {};
    DEFAULT_TABS.forEach((t) => (m[t.key] = t.defaultLabel));
    setEditLabels(m);
  };

  const ActiveComp = activeTab?.Component || (() => null);

  return (
    <div className="pt-wrap">
      {/* Top bar with tabs */}
      <Row style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="pt-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div className="pt-nav">
              <Nav pills className="flex-wrap" style={{ gap: 8 }}>
                {tabs.map((t) => (
                  <NavItem key={t.key}>
                    <NavLink
                      className={`pt-pill ${active === t.key ? "active" : ""}`}
                      onClick={() => toggleTab(t.key)}
                    >
                      {t.label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <div className="pt-actions">
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={openRename}
                >
                  Rename Tabs
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Content */}
      <Row>
        <Col sm="12">
          <Card className="pt-card">
            <CardBody className="pt-content">
              <ActiveComp />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Rename Modal */}
      <Modal
        isOpen={modalOpen}
        toggle={closeRename}
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader toggle={closeRename}>Rename Tabs</ModalHeader>
        <ModalBody className="pt-rename-body">
          {DEFAULT_TABS.map((t) => (
            <div className="rowline" key={t.key}>
              <Label>{t.defaultLabel}</Label>
              <Input
                value={editLabels?.[t.key] ?? t.defaultLabel}
                onChange={(e) => handleLabelChange(t.key, e.target.value)}
                placeholder={`Enter label for "${t.defaultLabel}"`}
              />
            </div>
          ))}
        </ModalBody>
        <ModalFooter
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Button color="danger" outline onClick={handleResetLabels}>
            Reset to Defaults
          </Button>
          <div>
            <Button
              color="secondary"
              onClick={closeRename}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button color="primary" onClick={handleSaveLabels}>
              Save
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
}
